// The live channel to the server (socket.io, server/lib/channel.js): opened when the page starts and kept, socket.io
// reconnecting by itself. Its client library is the one the server serves with it, loaded by address, not imported:
// neither the bundle nor the import map may take it in.
// The page sits in the room of the webtop it shows, so the server can reach every page on a webtop at once:
// joinRoom() runs whenever the open webtop's record is set (setWebtop in state.js: a load, a switch, an unlock, a lock)
// and on every (re)connect, since a new connection has a new id and no rooms. Joining goes through the API, which
// checks this browser may view the webtop; a locked webtop or one stored in the browser leaves the room instead.
// channelId() answers this page's socket id, which a request sends along so the server can talk back while it works;
// null when the channel cannot be reached in time, and the request then goes without its notes. onChannel(event, fn)
// listens to an event, before or after the channel is open, and answers a function that stops it (an applet's
// listeners go with it: @webtop/api's channel.on).
import { isLocalSlug, slug, webtop } from './state.js';
import { api } from './ui.js';

const CLIENT = '/socket.io/socket.io.esm.min.js';
const CONNECT_MS = 1500; // how long a request waits for the channel before going without it

let socket = null;
let opening = null;
const listeners = [];
let joined = null; // `${socket id} ${slug}` of the room this page is in, or null

async function open() {
  const { io } = await import(CLIENT);
  // A WebSocket first, long polling only where that is blocked: polling would hold one of the browser's few HTTP/1.1
  // connections to the plain-HTTP LAN address for as long as the page is open.
  socket = io({ transports: ['websocket', 'polling'], tryAllTransports: true });
  for (const [event, fn] of listeners) socket.on(event, fn);
  socket.on('connect', () => { joined = null; joinRoom(); });
}

// Open the channel unless it is open or opening; false when its client could not be loaded (an older server, or
// none), and the next call tries again.
async function ready() {
  try {
    await (opening ??= open());
    return true;
  } catch (e) {
    opening = null;
    console.warn(`live channel: ${e.message}`);
    return false;
  }
}

export async function channelId() {
  if (!(await ready())) return null;
  if (socket.connected) return socket.id;
  return new Promise((resolve) => {
    const done = () => { clearTimeout(timer); resolve(socket.id); };
    const timer = setTimeout(() => { socket.off('connect', done); resolve(null); }, CONNECT_MS);
    socket.once('connect', done);
  });
}

export function onChannel(event, fn) {
  const entry = [event, fn];
  listeners.push(entry);
  socket?.on(event, fn);
  return () => {
    const i = listeners.indexOf(entry);
    if (i >= 0) listeners.splice(i, 1);
    socket?.off(event, fn);
  };
}

// Be in the room of the webtop on screen, or in none: a webtop of the server whose record this page holds joins, a
// locked one (no record) or one stored in the browser leaves. Nothing is sent when that is already so. A failure only
// costs the room until the next change or reconnect.
export async function joinRoom() {
  if (!socket?.connected) return; // the connect handler joins
  const id = socket.id;
  const want = webtop?.slug === slug && !isLocalSlug(slug) ? slug : null;
  const key = want && `${id} ${want}`;
  if (key === joined || (!want && !joined)) return;
  joined = key;
  try {
    if (want) await api('PUT', `/api/webtops/${want}/channel`, { channel: id });
    else await api('DELETE', '/api/channel', { channel: id });
  } catch (e) {
    if (joined === key) joined = null;
    if (!e.stale) console.warn(`live channel: ${want ? `joining ${want}` : 'leaving'}: ${e.message}`);
  }
}

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
export function init() {
  ready();
}
